import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, Save } from 'lucide-react'
import { useMemo, useState } from 'react'

import { isApiError } from '@/api/client'
import { MAX_STAGE_NAME, MAX_STAGE_NOTES, putStageDetails, stageDetailsQuery, stageKeys, stagesQuery, type StageDetails, type StageDetailsPatch, type StageRow } from '@/api/stages'
import { Button } from '@/components/ui/button'
import { Field, Input, Textarea } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { EnabledSwitch } from '@/features/actions/parts'

import { NO_WORLD, worldLabel } from './words'

/**
 * The Details tab: the name stories and the studio call this place, the world it belongs to
 * (stories set in that world may pick it), whether stories may use it at all, and notes for
 * yourself. Saved with the revision it was read at; a `409` offers to load the newer version.
 */

const SELECT = 'h-11 w-full rounded-xl border border-line bg-paper px-3 text-sm text-ink focus-visible:focus-ring'

export interface DetailsTabProps {
  uid: string
  row: StageRow | null
}

export function DetailsTab({ uid, row }: DetailsTabProps) {
  const details = useQuery(stageDetailsQuery(uid))
  if (details.isPending) return <p className="card p-6 text-ink-2">Opening the details…</p>
  if (details.isError || !details.data) {
    return (
      <div className="card flex flex-col items-start gap-3 p-6" role="alert">
        <p className="text-ink">The details could not be loaded.</p>
        <Button variant="secondary" onClick={() => void details.refetch()}>
          Try again
        </Button>
      </div>
    )
  }
  // Keyed by rev: a newer version (after a save or "Load theirs") starts a fresh form.
  return <DetailsForm key={details.data.rev} uid={uid} row={row} saved={details.data} onReload={() => void details.refetch()} />
}

function DetailsForm({ uid, row, saved, onReload }: { uid: string; row: StageRow | null; saved: StageDetails; onReload(): void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<StageDetailsPatch>(() => pick(saved))
  const [conflict, setConflict] = useState(false)

  const all = useQuery(stagesQuery({}))
  const worlds = useMemo(() => {
    // `other` is the list's bucket for stages with no world, not a world to file one under.
    const ids = new Set<string>((all.data?.worlds ?? []).map((w) => w.id).filter((id) => id !== NO_WORLD))
    for (const r of all.data?.items ?? []) if (r.world) ids.add(r.world)
    if (saved.world) ids.add(saved.world)
    if (form.world) ids.add(form.world)
    return [...ids].sort((a, b) => worldLabel(a).localeCompare(worldLabel(b)))
  }, [all.data, saved.world, form.world])

  const changed = JSON.stringify(form) !== JSON.stringify(pick(saved))
  const nameMissing = form.name.trim() === ''
  // The picture's own name, offered back when this one was renamed (saving it = no rename).
  const ownName = saved.catalog_name ?? ''
  const renamed = ownName !== '' && form.name.trim() !== ownName

  const save = useMutation({
    mutationFn: () => putStageDetails(uid, saved.rev, { ...form, name: form.name.trim(), notes: form.notes }),
    onSuccess: (next) => {
      qc.setQueryData(stageKeys.details(uid), next)
      void qc.invalidateQueries({ queryKey: [...stageKeys.all(), 'list'] })
      void qc.invalidateQueries({ queryKey: stageKeys.one(uid) })
      toast.success('Details saved')
    },
    onError: (err) => {
      if (isApiError(err) && err.isConflict) return setConflict(true)
      toast.error('The details could not be saved', { description: err instanceof Error ? err.message : undefined })
    },
  })

  const set = (patch: Partial<StageDetailsPatch>) => setForm((f) => ({ ...f, ...patch }))

  return (
    <form
      className="card flex max-w-2xl flex-col gap-5 p-5"
      data-testid="details-tab"
      onSubmit={(e) => {
        e.preventDefault()
        if (changed && !nameMissing) save.mutate()
      }}
    >
      {conflict ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-warn/40 bg-warn-soft px-4 py-3 text-sm" data-testid="details-conflict">
          <span className="flex-1 text-ink">Someone changed these details while you were typing.</span>
          <Button size="sm" variant="secondary" onClick={onReload}>
            Load their version
          </Button>
        </div>
      ) : null}

      <Field label="Name" htmlFor="stage-name" hint="What the studio calls this place." error={nameMissing ? 'Give it a name.' : undefined}>
        <Input id="stage-name" value={form.name} onChange={(e) => set({ name: e.target.value })} aria-invalid={nameMissing} maxLength={MAX_STAGE_NAME} />
        {renamed ? (
          <button type="button" className="self-start rounded-sm text-xs font-medium text-accent hover:underline focus-visible:focus-ring" onClick={() => set({ name: ownName })} data-testid="own-name">
            Use its own name: “{ownName}”
          </button>
        ) : null}
      </Field>

      <Field
        label="World"
        htmlFor="stage-world"
        hint={row?.catalog_world && row.catalog_world !== form.world ? `Its picture comes from ${worldLabel(row.catalog_world)}. Stories set in the world you pick may use it.` : 'Stories set in this world may use it.'}
      >
        <select id="stage-world" className={SELECT} value={form.world} onChange={(e) => set({ world: e.target.value })}>
          {form.world === '' ? <option value="">Choose a world</option> : null}
          {worlds.map((w) => (
            <option key={w} value={w}>
              {worldLabel(w)}
            </option>
          ))}
        </select>
      </Field>

      <div className="flex items-center gap-3 rounded-xl border border-line p-3">
        <EnabledSwitch checked={form.enabled} onChange={(enabled) => set({ enabled })} label="Used in stories" />
        <span className="flex flex-col text-sm">
          <span className="font-medium text-ink">{form.enabled ? 'Used in stories' : 'Not used in stories'}</span>
          <span className="text-xs text-ink-2">{form.enabled ? 'New stories may pick this background.' : 'New stories never pick it. Stories already made keep playing.'}</span>
        </span>
      </div>

      <Field label="Notes" htmlFor="stage-notes" hint="Only for you — stories never read them.">
        <Textarea id="stage-notes" value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={4} maxLength={MAX_STAGE_NOTES}placeholder="e.g. The pond is small — keep swimming stories elsewhere." />
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={!changed || nameMissing || conflict} loading={save.isPending} data-testid="save-details">
          {save.isPending ? null : <Save aria-hidden />}
          Save details
        </Button>
        {changed ? (
          <Button type="button" variant="ghost" onClick={() => setForm(pick(saved))}>
            <RotateCcw aria-hidden />
            Undo changes
          </Button>
        ) : null}
      </div>
    </form>
  )
}

function pick(d: StageDetails): StageDetailsPatch {
  return { name: d.name, world: d.world, enabled: d.enabled, notes: d.notes }
}
